import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, MapPin, ScanLine, XCircle } from 'lucide-react';
import { layoutDots } from '../../utils/constellationLayout';
import { haversineMeters } from '../../utils/exifGps';

const VIEWPORT = { width: 200, height: 80, padding: 14 };
const FA_DIGITS = ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸'];

/**
 * Small geographic-agreement viz + headline above the upload gallery.
 * State drives color scheme + which messaging is shown.
 *
 * New states this banner now handles (beyond the original empty/loading/
 * ok/missing/mismatch):
 *   - extracting     — exifr returned null and the server is reading the
 *                       on-pixel stamp; shows a scanning-line icon.
 *   - stamp source   — when at least one coord came from the stamp reader
 *                       (not EXIF), a small chip surfaces under the
 *                       headline so the admin reviewer sees provenance.
 */
export default function ConstellationBanner({ coords, validation }) {
  const validPoints = coords.filter((c) => c && typeof c.lat === 'number');
  const dots = layoutDots(validPoints, VIEWPORT);
  const farthestPair = validation?.farthestPair || null;

  // Outer banner state controls color scheme.
  const state = validation?.kind || 'empty';

  // Count how many resolved coords came from stamp extraction (not EXIF).
  const stampCount = coords.filter((c) => c && c.source === 'stamp').length;
  const exifCount = coords.filter((c) => c && c.source === 'exif').length;

  return (
    <motion.div
      className={`constellation-banner state-${state}`}
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: 'easeOut' }}
    >
      <svg
        className="constellation-viz"
        viewBox={`0 0 ${VIEWPORT.width} ${VIEWPORT.height}`}
        role="img"
        aria-label="نمایش موقعیت نسبی عکس‌ها"
      >
        {/* Lines between pairs */}
        {validPoints.length >= 2 && dots.map((a, i) =>
          dots.slice(i + 1).map((b, jOffset) => {
            const j = i + 1 + jOffset;
            const dMeters = haversineMeters(validPoints[i], validPoints[j]);
            const isOffender =
              farthestPair && (
                (farthestPair[0] === i && farthestPair[1] === j) ||
                (farthestPair[0] === j && farthestPair[1] === i)
              ) && validation.kind === 'mismatch';
            return (
              <g key={`line-${i}-${j}`}>
                <line
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  className={`constellation-edge${isOffender ? ' offender' : ''}`}
                />
                <text
                  x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 4}
                  className="constellation-edge-label"
                  textAnchor="middle"
                >
                  {Math.round(dMeters)} m
                </text>
              </g>
            );
          })
        )}
        {/* Dots with numeric labels */}
        {dots.map((d, i) => (
          <g key={`dot-${i}`}>
            <circle cx={d.x} cy={d.y} r={6} className="constellation-dot" />
            <text x={d.x} y={d.y + 3} textAnchor="middle" className="constellation-dot-label">
              {FA_DIGITS[i]}
            </text>
          </g>
        ))}
      </svg>

      <div className="constellation-text">
        <AnimatePresence mode="wait">
          <motion.div
            key={state}
            className="constellation-headline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {state === 'ok' && (
              <>
                <CheckCircle2 size={16} />
                <span>تأیید موقعیت</span>
              </>
            )}
            {state === 'mismatch' && (
              <>
                <XCircle size={16} />
                <span>عدم تطابق موقعیت</span>
              </>
            )}
            {state === 'missing' && (
              <>
                <XCircle size={16} />
                <span>موقعیت ناقص</span>
              </>
            )}
            {state === 'loading' && (
              <>
                <MapPin size={16} className="spin" />
                <span>خواندن موقعیت…</span>
              </>
            )}
            {state === 'extracting' && (
              <>
                <ScanLine size={16} className="constellation-scan-icon" />
                <span>خواندن مهر روی تصویر…</span>
              </>
            )}
            {state === 'empty' && (
              <>
                <MapPin size={16} />
                <span>منتظر تصویر</span>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {validation?.message && (
          <div className="constellation-body">{validation.message}</div>
        )}
        {state === 'ok' && validation.maxMeters > 0 && (
          <div className="constellation-body">
            بیشترین فاصله: {Math.round(validation.maxMeters)} متر
          </div>
        )}

        {/* Provenance chip: shown whenever at least one coord came from the
            stamp reader. Quietly tells the reviewer "this didn't come from
            EXIF; verify if the values look off." */}
        {stampCount > 0 && (state === 'ok' || state === 'mismatch') && (
          <div className="constellation-source-chip" title="منبع مختصات">
            <ScanLine size={11} aria-hidden />
            <span>
              {stampCount === coords.length
                ? 'همهٔ مختصات از مهرِ روی تصویر استخراج شد (EXIF نداشتند)'
                : `${toFa(stampCount)} از ${toFa(coords.length)} عکس از مهرِ روی تصویر استخراج شد${exifCount ? '؛ بقیه از EXIF' : ''}`}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function toFa(n) {
  return String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}
